import { Link } from "react-router-dom";
import { Linkedin, BookOpen, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import ReviewsCarousel from "@/components/ReviewsCarousel";
import { T } from "@/lib/texts";

export default function ChiSono() {
  return (
    <>
    <section className="section-pad">
      <SEO
        path="/chi-sono"
        title="Chi Sono"
        description="Roberta Castagna, Consulente d'Impresa e Brand Strategist. Esperienza diretta sul campo in ristorazione, retail, wellness, liquoristica, servizi e turismo di territorio. Aiuto le imprese a costruire strutture organizzative fluide, solide e orientate alla crescita."
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5" data-reveal="left">
            <div className="aspect-[3/4] overflow-hidden sticky top-28">
              <img data-testid="chisono-portrait" src="https://customer-assets.emergentagent.com/job_process-gap/artifacts/06tz6h9s_Foto%20Profilo.webp" alt="Roberta Castagna" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7" data-reveal="right" data-reveal-delay="1">
            <p className="eyebrow mb-4">Chi Sono</p>
            <h1 className="font-display text-5xl sm:text-6xl leading-[1.05] text-[#2A3229]">
              Roberta <span className="text-[#323C36]">Castagna</span>
            </h1>
            <p className="mt-8 text-lg text-[#5A6058] leading-relaxed">
              <T k="chisono_intro">Sono <strong className="text-[#2A3229]">Roberta Castagna</strong>, Consulente d'Impresa e Brand Strategist, e aiuto le Piccole e Medie Imprese a rompere questo circolo vizioso per edificare una struttura organizzativa fluida, solida e orientata alla crescita.</T>
            </p>

            <div className="mt-10 space-y-6 text-[#2A3229] leading-relaxed">
              <p>La mia strada non è nata dietro una cattedra, ma direttamente sul campo, vivendo le dinamiche aziendali dall'interno prima di diventarne consulente. Ho coordinato team, ottimizzato processi operativi e riorganizzato flussi in settori ad altissima complessità e con logiche totalmente differenti tra loro: <strong className="text-[#323C36]">dalla ristorazione al retail, dal wellness alla liquoristica, fino ai servizi e al turismo di territorio.</strong></p>
              <p>Questa trasversalità mi ha permesso di sviluppare un occhio analitico unico. Ho imparato a guardare un'azienda da due prospettive speculari: dall'interno, analizzando con precisione i flussi, i ruoli e gli strumenti che muovono la macchina; dall'esterno, comprendendo le leve del posizionamento di Marca e del consumatore finale.</p>
              <p>Non credo nelle soluzioni di facciata o nei compromessi che rinviano solo il problema. Quando entro in un'azienda, il mio obiettivo è fare chiarezza totale. Questo richiede il coraggio di scardinare le vecchie abitudini, interrompere i flussi inefficienti e smantellare quelle dinamiche strutturali che fino ad oggi hanno frenato il potenziale dell'impresa.</p>
              <p>Non è un processo passivo: è una ristrutturazione profonda, guidata da un pragmatismo assoluto. Smonto ciò che crea attrito e lo ricostruisco su basi nuove, introducendo ordine, ruoli chiari e automatismi.</p>
              <p>Il mio obiettivo finale è <strong className="text-[#323C36]">restituire all'imprenditore il controllo strategico e la libertà di far crescere il proprio business</strong>, trasformando l'azienda in un sistema virtuoso capace di generare valore in modo costante, prevedibile e strutturato.</p>
            </div>

            <div className="mt-12 grid sm:grid-cols-3 gap-6">
              {[
                {k:"Processi", v:"Mappatura, misurazione e ridisegno dei flussi operativi"},
                {k:"Organizzazione", v:"Ruoli, deleghe e strutture funzionali al business"},
                {k:"Persone", v:"Team allineati, autonomi, coinvolti nel cambiamento"},
              ].map((b,i) => (
                <div key={i} className="border-t border-[#323C36]/25 pt-4">
                  <p className="font-display text-xl text-[#323C36]">{b.k}</p>
                  <p className="text-sm text-[#5A6058] mt-2">{b.v}</p>
                </div>
              ))}
            </div>

            <div className="mt-12">
              <a data-testid="chisono-contact" href="https://calendar.app.google/wMsDARnKAyyi4doX6" target="_blank" rel="noreferrer" className="btn-primary">Prenota una sessione strategica</a>
            </div>

            {/* APPROFONDISCI — autorità */}
            <div className="mt-14 grid sm:grid-cols-2 gap-6">
              <a
                data-testid="chisono-linkedin"
                href="https://www.linkedin.com/in/roberta-castagna/"
                target="_blank" rel="noreferrer"
                className="group border border-[#323C36]/15 bg-white p-7 transition-all duration-500 hover:-translate-y-1 hover:border-[#323C36] hover:shadow-lg"
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="w-11 h-11 rounded-full bg-[#323C36] text-[#F6F3EF] flex items-center justify-center">
                    <Linkedin size={18} />
                  </span>
                  <ArrowUpRight size={16} className="text-[#A8674F] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
                <p className="eyebrow mb-2">Il mio percorso</p>
                <p className="font-display text-xl text-[#2A3229] leading-snug mb-3">Oltre 10 anni di management sul campo</p>
                <p className="text-sm text-[#5A6058] leading-relaxed">Ruoli, settori e progetti documentati: approfondisci la mia esperienza professionale su LinkedIn. <span className="text-[#5A6058]/70">(si apre in una nuova scheda)</span></p>
              </a>
              <a
                data-testid="chisono-libreria"
                href="https://www.amazon.it/-/en/hz/wishlist/ls/3R5EF44QOK9SO?&linkCode=ll2&tag=robertacastag-21&linkId=55c2ab59481b416d58968e5926822a02&ref_=as_li_ss_tl"
                target="_blank" rel="noreferrer"
                className="group border border-[#323C36]/15 bg-white p-7 transition-all duration-500 hover:-translate-y-1 hover:border-[#323C36] hover:shadow-lg"
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="w-11 h-11 rounded-full bg-[#A8674F] text-[#F6F3EF] flex items-center justify-center">
                    <BookOpen size={18} />
                  </span>
                  <ArrowUpRight size={16} className="text-[#A8674F] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
                <p className="eyebrow mb-2">Lifestyle &amp; Produttività</p>
                <p className="font-display text-xl text-[#2A3229] leading-snug mb-3">La mia libreria consigliata</p>
                <p className="text-sm text-[#5A6058] leading-relaxed">I libri che consiglio più spesso agli imprenditori che affianco. <span className="text-[#5A6058]/70">(link affiliato Amazon)</span></p>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* RECENSIONI GOOGLE */}
    <section data-testid="chisono-reviews" className="relative overflow-hidden bg-[#EDE7DD] py-14 sm:py-16">
      <img
        src="https://customer-assets-v7afamib.emergentagent.net/job_process-gap/artifacts/f9hrwe9q_STORIES%20CHALLENGE%20FEBBRAIO%20%285%29.png"
        alt=""
        aria-hidden="true"
        className="absolute -left-10 top-1/2 -translate-y-1/2 w-56 max-w-none opacity-[0.07] pointer-events-none select-none"
      />
      <div className="max-w-2xl mx-auto px-6 lg:px-10 text-center relative" data-reveal>
        <p className="eyebrow mb-4">Dicono di me</p>
        <h2 className="font-display text-3xl sm:text-4xl text-[#2A3229] leading-tight mb-10">
          Le parole di chi ha <span className="text-[#A8674F]">lavorato con me</span>.
        </h2>
        <ReviewsCarousel compact />
        <div className="mt-8">
          <a
            data-testid="chisono-reviews-link"
            href="https://share.google/kOfk1mZ4wFh9qx5TN"
            target="_blank" rel="noreferrer"
            className="text-[#323C36] link-underline text-sm uppercase tracking-widest"
          >
            Tutte le recensioni su Google →
          </a>
        </div>
      </div>
    </section>
    </>
  );
}
